import { useEffect, useRef, useCallback } from 'react';
// import { base44 } from '@/api/base44Client';
import { useQueryClient } from '@tanstack/react-query';
import * as userApi from '../../services/userApi';

const base44 = {
    entities: {
        Alert: { subscribe: (cb) => { return () => { } } },
        Notification: { subscribe: (cb) => { return () => { } } },
        TermsVersion: { subscribe: (cb) => { return () => { } } }
    }
}; // MOCK

/**
 * Real-time alerts hook
 * Handles WebSocket-like subscriptions for alerts and notifications
 * 
 * RULES:
 * - Only activates if user has accepted terms
 * - Only triggers for folders user has enabled AND active
 * - Does not replay old alerts on reconnect (no toast replay)
 * - Auto-reconnects and syncs via API on connection restore
 */
export function useRealtimeAlerts({
    userId,
    hasAcceptedTerms,
    folders,
    onNewAlert,
    onNewNotification
}) {
    const queryClient = useQueryClient();
    const lastAlertTimeRef = useRef(new Date().toISOString());
    const isConnectedRef = useRef(false);
    const foldersRef = useRef(folders);

    // Keep folders ref updated
    useEffect(() => {
        foldersRef.current = folders;
    }, [folders]);

    // Get enabled folder IDs (uses ref to avoid stale closure)
    const getEnabledFolderIds = useCallback(() => {
        return foldersRef.current
            .filter(f => f.is_enabled && f.is_active)
            .map(f => f.id);
    }, []);

    // Check if user can receive alerts for a folder
    const canReceiveAlertForFolder = useCallback((folderId) => {
        const enabledIds = getEnabledFolderIds();
        return enabledIds.includes(folderId);
    }, [getEnabledFolderIds]);

    // Handle new alert event
    const handleNewAlert = useCallback(async (event) => {
        if (event.type !== 'create') return;

        const newAlert = event.data;

        // Check if alert is newer than our connection time (don't replay old)
        if (new Date(newAlert.created_date) < new Date(lastAlertTimeRef.current)) {
            return;
        }

        // Check if user can receive this alert
        if (!canReceiveAlertForFolder(newAlert.folder_id)) {
            return;
        }

        // Create notification in database
        const result = await userApi.createNotificationIfEligible(
            userId,
            newAlert.id,
            newAlert.folder_id,
            newAlert
        );

        // Trigger callbacks for UI updates
        if (result.show_toast) {
            onNewAlert?.(newAlert);
        }

        // Refresh queries
        queryClient.invalidateQueries({ queryKey: ['userAlerts'] });
        queryClient.invalidateQueries({ queryKey: ['allAlerts'] });
        queryClient.invalidateQueries({ queryKey: ['userNotifications'] });
    }, [userId, canReceiveAlertForFolder, onNewAlert, queryClient]);

    // Handle new notification event
    const handleNewNotification = useCallback((event) => {
        if (event.type !== 'create') return;

        const notification = event.data;

        // Only handle notifications for this user
        if (notification.user_id !== userId) return;

        onNewNotification?.(notification);
        queryClient.invalidateQueries({ queryKey: ['userNotifications'] });
    }, [userId, onNewNotification, queryClient]);

    // Subscribe to real-time events
    useEffect(() => {
        // Don't connect if terms not accepted - SECURITY: block real-time if terms not accepted
        if (!hasAcceptedTerms || !userId) {
            isConnectedRef.current = false;
            return;
        }

        // Set connection time for filtering old alerts (no toast replay)
        lastAlertTimeRef.current = new Date().toISOString();
        isConnectedRef.current = true;

        // Subscribe to Alert entity changes
        const unsubscribeAlerts = base44.entities.Alert.subscribe(handleNewAlert);

        // Subscribe to Notification entity changes
        const unsubscribeNotifications = base44.entities.Notification.subscribe(handleNewNotification);

        // On reconnect, sync data via API (not replay toasts)
        queryClient.invalidateQueries({ queryKey: ['userAlerts'] });
        queryClient.invalidateQueries({ queryKey: ['userNotifications'] });
        queryClient.invalidateQueries({ queryKey: ['allAlerts'] });

        // Cleanup on unmount or when terms status changes
        return () => {
            isConnectedRef.current = false;
            unsubscribeAlerts();
            unsubscribeNotifications();
        };
    }, [hasAcceptedTerms, userId, handleNewAlert, handleNewNotification, queryClient]);

    // Re-sync on reconnect (folders change triggers re-evaluation)
    useEffect(() => {
        if (isConnectedRef.current && hasAcceptedTerms) {
            // Refresh data when folder subscriptions change
            queryClient.invalidateQueries({ queryKey: ['userAlerts'] });
            queryClient.invalidateQueries({ queryKey: ['userNotifications'] });
        }
    }, [folders, hasAcceptedTerms, queryClient]);

    return {
        isConnected: isConnectedRef.current,
        enabledFolderIds: getEnabledFolderIds()
    };
}

/**
 * Hook to monitor terms version changes
 * Forces re-acceptance if new terms are published
 */
export function useTermsVersionMonitor({
    userId,
    currentTermsVersionId,
    onTermsUpdated
}) {
    const lastCheckedRef = useRef(currentTermsVersionId);

    useEffect(() => {
        if (!userId) return;

        // Subscribe to TermsVersion changes
        const unsubscribe = base44.entities.TermsVersion.subscribe(async (event) => {
            if (event.type === 'create') {
                // New terms version created - force re-acceptance
                onTermsUpdated?.();
            }
        });

        return () => unsubscribe();
    }, [userId, onTermsUpdated]);

    // Check if terms version changed
    useEffect(() => {
        if (currentTermsVersionId && currentTermsVersionId !== lastCheckedRef.current) {
            lastCheckedRef.current = currentTermsVersionId;
        }
    }, [currentTermsVersionId]);
}
